import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { RouterService } from '@bca-core/services/router.service';
import { MessageType } from '@bca-shared/models/_enums';
import { APP_CONFIG } from '@bca-shared/models/config';
import { IResponse } from '@bca-shared/models/response';
import * as appActions from '@bca-store/app.actions';
import { environment } from '@environments/environment';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, filter, tap } from 'rxjs/operators';
import { createAction } from '@bca-store/models/action-creator';
import { IAction } from '@bca-store/models/action';

@Injectable()
export class NotificationInterceptor implements HttpInterceptor {
  constructor(private _injector: Injector) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      filter((event: HttpEvent<IResponse>) => event instanceof HttpResponse),
      tap((response: HttpResponse<IResponse>) => {
        const { error, success, info } = response.body;
        const translate = this._injector.get(TranslateService);

        const store = this._injector.get(Store);
        if (!success && error) {
          const { code, message } = error;
          const translatedBody = translate.instant(`NOTIFICATION.ERROR.${code}`) as string;

          const body = translatedBody.includes('NOTIFICATION')
            ? message
              ? message
              : null
            : translatedBody;

          if (!body) {
            throw Error();
          }

          store.dispatch(
            this._createAction(
              new appActions.SetMessage({
                body,
                type: MessageType.ERROR,
                context: { request, response }
              })
            )
          );
        } else if (!success && info) {
          const { code, message } = info;
          const translatedBody = translate.instant(`NOTIFICATION.INFO.${code}`) as string;

          const body = translatedBody.includes('NOTIFICATION')
            ? message
              ? message
              : null
            : translatedBody;

          if (!body) {
            throw Error();
          }

          store.dispatch(
            this._createAction(
              new appActions.SetMessage({
                body,
                type: MessageType.INFO,
                context: { request, response }
              })
            )
          );
        }
      }),
      catchError((response: HttpErrorResponse) => {
        const { production } = environment;
        const store = this._injector.get(Store);
        const router = this._injector.get(RouterService);

        if (production && response.status === 503) {
          const { redirect_url } = this._injector.get(APP_CONFIG);
          router.redirectToUrl(redirect_url);
          return EMPTY;
        }

        // do not show error message to user if a 404 occurs on an asset
        if (response.status === 404 && response.url.includes('assets')) {
          return throwError(response);
        }

        const message = {
          body: 'NOTIFICATION.ERROR.GENERAL',
          type: MessageType.ERROR,
          context: { request, response },
          originalException: response
        };

        store.dispatch(this._createAction(new appActions.SetMessage(message)));
        return throwError(response);
      })
    );
  }

  private _createAction(action: IAction): IAction {
    return createAction(action, NotificationInterceptor);
  }
}
